// Phase 14e-a — /admin/rates/facility-overrides.
//
// Per-FacilityContract bill-rate override; the simplest of the 3 rate
// resources. Operator picks a facility + role, sets a bill-rate override
// that supersedes the rate-card's defaultBillRate for that facility's
// shifts. Setting is always LTC (facilities never post HOMECARE), so it's
// injected, not asked. Pay rate is NOT overridden (caregivers see the same
// pay regardless of facility per Q2 / 11-A).

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { unwrap } from '@truecare/api-client';
import type { AdminFacilityListItem } from '@truecare/schemas';
import { api } from '../../api';
import { SupersedableResourcePanel } from '../../components/SupersedableResourcePanel';

export const Route = createFileRoute('/rates/facility-overrides')({
  component: FacilityOverridesPage,
});

// Facility shifts are always LTC (BuyerShiftPanel hardcodes setting='LTC' for the
// FACILITY persona — a facility never posts HOMECARE), so the override form
// doesn't ask for a setting; the create body injects 'LTC' below.
const OVERRIDE_SETTING = 'LTC' as const;

// Shift-time-bucket options. '' = Default / all shifts (the backend column is
// nullable — the create body converts '' → null below). The three buckets are
// the canonical shift-time partition (DAY 07:00–14:59, EVENING 15:00–22:59,
// NIGHT 23:00–06:59 local, by START hour); a bucket-specific override applies
// only to shifts whose local start falls in that window.
const SHIFT_BUCKET_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'DAY', label: 'Day' },
  { value: 'EVENING', label: 'Evening' },
  { value: 'NIGHT', label: 'Night' },
] as const;

// The backend create schema takes roleId (uuid) + shiftTimeBucket, NOT a role
// CODE — roles are loaded live from /admin/roles below (same source the rate-card
// form uses). A hardcoded code list here shipped the wrong field name + omitted
// shiftTimeBucket, which 422'd every create; see Flow 12 for the regression guard.
const CREATE_SCHEMA = z.object({
  facilityId: z.string().uuid(),
  roleId: z.string().uuid('Pick a role'),
  shiftTimeBucket: z.enum(['', 'DAY', 'EVENING', 'NIGHT']),
  billRateOverride: z.string().regex(/^\d+(\.\d{1,4})?$/),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

const SUPERSEDE_SCHEMA = z.object({
  newBillRateOverride: z.string().regex(/^\d+(\.\d{1,4})?$/),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

const DEPRECATE_SCHEMA = z.object({
  effectiveTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

// Edit (unused rows) — bill rate only; identity tuple + dates frozen.
const EDIT_SCHEMA = z.object({
  billRateOverride: z.string().regex(/^\d+(\.\d{1,4})?$/, 'Enter a positive decimal'),
});

interface FacilityOverrideRow {
  id: string;
  facilityId: string;
  facilityName: string;
  roleCode: string;
  setting: string;
  billRateOverride: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isCurrent: boolean;
  inUse: boolean;
}

function FacilityOverridesPage() {
  // Facilities for the create-form picker (replaces pasting a raw UUID).
  const facilitiesQuery = useQuery({
    queryKey: ['admin', 'facilities', 'override-picker'],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/admin/facilities', {
          params: { query: { page: 1, pageSize: 100 } },
        } as never),
      ) as Promise<{ items: AdminFacilityListItem[] }>,
  });
  const facilityOptions = (facilitiesQuery.data?.items ?? []).map((f) => ({
    value: f.id,
    label: f.name,
  }));

  // Roles for the create-form picker — value is the role UUID (what the backend
  // create schema wants), not the code.
  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles', { activeOnly: true }],
    queryFn: async () => {

      const r = (await unwrap(api.GET('/api/v1/admin/roles' as never))) as {
        items: { id: string; code: string; name: string }[];
      };
      return r;
    },
  });
  const roleOptions = (rolesQuery.data?.items ?? []).map((r) => ({
    value: r.id,
    label: `${r.code} — ${r.name}`,
  }));

  return (
    <SupersedableResourcePanel<FacilityOverrideRow>
      labels={{
        panelTitle: 'Facility bill-rate overrides',
        singular: 'override',
        plural: 'overrides',
      }}
      columns={[
        { key: 'facilityName', header: 'Facility', width: 'w-48' },
        { key: 'roleCode', header: 'Role', width: 'w-24' },
        {
          key: 'billRateOverride',
          header: 'Bill rate ($/hr)',
          width: 'w-32',
          render: (r) => `$${r.billRateOverride}`,
        },
        { key: 'effectiveFrom', header: 'From', width: 'w-28' },
        {
          key: 'effectiveTo',
          header: 'To',
          width: 'w-28',
          render: (r) => r.effectiveTo ?? '—',
        },
      ]}
      endpoints={{
        list: async () => {
           
          const r = (await unwrap(
            api.GET('/api/v1/admin/rates/facility-overrides' as never, {
              params: { query: { includeHistorical: 'true' } },
            } as never),
          )) as { items: FacilityOverrideRow[] };
          return r;
        },
        create: (body) =>
           
          unwrap(
            api.POST('/api/v1/admin/rates/facility-overrides' as never, {
              // Inject the always-LTC setting; convert the '' all-day sentinel to
              // the null the backend column expects.
              body: {
                ...body,
                setting: OVERRIDE_SETTING,
                shiftTimeBucket: (body as { shiftTimeBucket?: string }).shiftTimeBucket || null,
              },
            } as never),
          ),
        supersede: (id, body) =>
           
          unwrap(
            api.POST('/api/v1/admin/rates/facility-overrides/{id}/supersede' as never, {
              params: { path: { id } },
              body,
            } as never),
          ),
        deprecate: (id, body) =>

          unwrap(
            api.POST('/api/v1/admin/rates/facility-overrides/{id}/deprecate' as never, {
              params: { path: { id } },
              body,
            } as never),
          ),
        edit: (id, body) =>
          unwrap(
            api.PATCH('/api/v1/admin/rates/facility-overrides/{id}' as never, {
              params: { path: { id } },
              body,
            } as never),
          ),
        delete: (id) =>
          unwrap(
            api.DELETE('/api/v1/admin/rates/facility-overrides/{id}' as never, {
              params: { path: { id } },
            } as never),
          ),
      }}
      form={{
        createSchema: CREATE_SCHEMA,
        createFields: [
          {
            kind: 'select',
            name: 'facilityId',
            label: 'Facility',
            options: facilityOptions,
            help: facilityOptions.length === 0 ? 'Loading facilities…' : undefined,
          },
          {
            kind: 'select',
            name: 'roleId',
            label: 'Role',
            options: roleOptions,
            help: roleOptions.length === 0 ? 'Loading roles…' : undefined,
          },
          {
            kind: 'select',
            name: 'shiftTimeBucket',
            label: 'Shift time',
            options: SHIFT_BUCKET_OPTIONS,
            help: 'Default = the base rate for every shift at this facility (night/weekend/holiday premiums still stack on top). Pick Day/Evening/Night only if the deal covers just that window.',
          },
          { kind: 'money', name: 'billRateOverride', label: 'Bill rate override' },
        ],
        supersedeSchema: SUPERSEDE_SCHEMA,
        supersedeFields: [
          {
            kind: 'money',
            name: 'newBillRateOverride',
            label: 'New bill rate',
            help: 'Pre-filled with the current rate — change it to the new value.',
          },
        ],
        supersedeDefaultValues: (entry) => ({ newBillRateOverride: entry.billRateOverride }),
        deprecateSchema: DEPRECATE_SCHEMA,
        editSchema: EDIT_SCHEMA,
        editFields: [{ kind: 'money', name: 'billRateOverride', label: 'Bill rate override' }],
        editDefaultValues: (entry) => ({ billRateOverride: entry.billRateOverride }),
      }}
      query={{ queryKey: ['admin', 'rates', 'facility-overrides'] }}
    />
  );
}
